import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Users,
  ArrowLeft,
  Banknote,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Phone,
  MapPin,
  Calendar,
  RotateCcw,
  CreditCard,
  Search,
  CheckCircle2,
  Clock,
  Printer,
  FileSpreadsheet,
  AlertCircle,
  Wallet,
  Coins,
  History,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from '../components/PharmacyLogo';
import DateFilter from '../components/DateFilter';
import SearchableSelect from '../components/SearchableSelect';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

function fmtDate(d) {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(d) {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtMoney(amount) {
  const n = parseFloat(amount) || 0;
  return `Rs. ${n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function StaffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();

  const [staff, setStaff] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'salaries' | 'advances'

  // Modals
  const [showPaySalary, setShowPaySalary] = useState(false);
  const [showPayAdvance, setShowPayAdvance] = useState(false);
  const [showDeductAdvance, setShowDeductAdvance] = useState(false);
  const [showEditStaff, setShowEditStaff] = useState(false);
  const [showDeleteStaff, setShowDeleteStaff] = useState(false);
  const [editTransaction, setEditTransaction] = useState(null);
  const [deleteTransaction, setDeleteTransaction] = useState(null);

  // Forms
  const [salaryForm, setSalaryForm] = useState({
    amount: '',
    deduction_amount: '0',
    payment_method: 'cash',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [advanceForm, setAdvanceForm] = useState({
    amount: '',
    payment_method: 'cash',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [deductForm, setDeductForm] = useState({
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [editForm, setEditForm] = useState({});
  const [editTransForm, setEditTransForm] = useState({});

  // Filter
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (id) {
      loadStaffData();
    }
  }, [id]);

  const loadStaffData = async () => {
    setLoading(true);
    try {
      const staffId = parseInt(id);
      const res = await api.get(`/staff/${staffId}`);
      if (res && res.staff) {
        setStaff(res.staff);
        setStats(res.stats || null);
      } else {
        setStaff(res);
      }

      // Load transactions
      const transRes = await api.get(`/staff-transactions?staff_id=${staffId}&limit=500`);
      const list = Array.isArray(transRes) ? transRes : (transRes?.transactions || []);
      setTransactions(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load staff details');
    } finally {
      setLoading(false);
    }
  };

  // Open Salary Modal
  const openPaySalaryModal = () => {
    const currentAdv = parseFloat(staff?.advanced_payment) || 0;
    setSalaryForm({
      amount: staff?.salary_amount || '0',
      deduction_amount: currentAdv > 0 ? String(Math.min(currentAdv, parseFloat(staff?.salary_amount) || 0)) : '0',
      payment_method: 'cash',
      transaction_date: new Date().toISOString().split('T')[0],
      notes: `Salary payment for ${staff?.name}`,
    });
    setShowPaySalary(true);
  };

  // Submit Salary
  const handlePaySalary = async (e) => {
    e.preventDefault();
    const amount = parseFloat(salaryForm.amount);
    const deduction = parseFloat(salaryForm.deduction_amount) || 0;
    if (!amount || amount <= 0) return toast.error('Please enter a valid salary amount');
    if (deduction > amount) return toast.error('Advance deduction cannot exceed salary amount');
    if (deduction > advanceBalance) return toast.error(`Advance deduction cannot exceed active advance of ${fmtMoney(advanceBalance)}`);

    try {
      await api.post('/staff-transactions', {
        staff_id: parseInt(id),
        type: 'salary',
        amount,
        deduction_amount: deduction,
        payment_method: salaryForm.payment_method,
        transaction_date: salaryForm.transaction_date,
        notes: salaryForm.notes,
        user_id: user?.id || 1,
      });

      const net = Math.max(0, amount - deduction);
      toast.success(`Salary of ${fmtMoney(net)} recorded successfully`);
      setShowPaySalary(false);
      await loadStaffData();
    } catch (err) {
      toast.error(err.message || 'Failed to record salary');
    }
  };

  // Open Advance Modal
  const openPayAdvanceModal = () => {
    setAdvanceForm({
      amount: '',
      payment_method: 'cash',
      transaction_date: new Date().toISOString().split('T')[0],
      notes: `Advance payment for ${staff?.name}`,
    });
    setShowPayAdvance(true);
  };

  // Submit Advance
  const handlePayAdvance = async (e) => {
    e.preventDefault();
    const amount = parseFloat(advanceForm.amount);
    if (!amount || amount <= 0) return toast.error('Please enter a valid advance amount');

    try {
      await api.post('/staff-transactions', {
        staff_id: parseInt(id),
        type: 'advance',
        amount,
        payment_method: advanceForm.payment_method,
        transaction_date: advanceForm.transaction_date,
        notes: advanceForm.notes,
        user_id: user?.id || 1,
      });

      toast.success(`Advance of ${fmtMoney(amount)} disbursed successfully`);
      setShowPayAdvance(false);
      await loadStaffData();
    } catch (err) {
      toast.error(err.message || 'Failed to record advance');
    }
  };

  // Open Deduct / Settle Advance Modal
  const openDeductAdvanceModal = () => {
    const currentAdv = parseFloat(staff?.advanced_payment) || 0;
    setDeductForm({
      amount: currentAdv > 0 ? String(currentAdv) : '',
      transaction_date: new Date().toISOString().split('T')[0],
      notes: `Advance settlement for ${staff?.name}`,
    });
    setShowDeductAdvance(true);
  };

  // Submit Deduct / Settle Advance
  const handleDeductAdvance = async (e) => {
    e.preventDefault();
    const amount = parseFloat(deductForm.amount);
    if (!amount || amount <= 0) return toast.error('Please enter a valid settlement amount');

    try {
      await api.post('/staff-transactions', {
        staff_id: parseInt(id),
        type: 'deduction',
        amount,
        payment_method: 'cash',
        transaction_date: deductForm.transaction_date,
        notes: deductForm.notes,
        user_id: user?.id || 1,
      });

      toast.success(`Advance balance settled by ${fmtMoney(amount)}`);
      setShowDeductAdvance(false);
      await loadStaffData();
    } catch (err) {
      toast.error(err.message || 'Failed to settle advance');
    }
  };

  // Open Edit Staff Modal
  const openEditStaffModal = () => {
    setEditForm({
      name: staff.name || '',
      phone: staff.phone || '',
      address: staff.address || '',
      age: staff.age || '',
      salary_amount: staff.salary_amount || '0',
      advanced_payment: staff.advanced_payment || '0',
      notes: staff.notes || '',
    });
    setShowEditStaff(true);
  };

  // Save Edit Staff
  const handleSaveStaff = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) return toast.error('Staff name is required');
    try {
      await api.put(`/staff/${staff.id}`, {
        name: editForm.name,
        phone: editForm.phone,
        address: editForm.address,
        age: editForm.age ? parseInt(editForm.age) : null,
        salary_amount: parseFloat(editForm.salary_amount) || 0,
        advanced_payment: parseFloat(editForm.advanced_payment) || 0,
        notes: editForm.notes,
      });

      toast.success('Staff profile updated successfully');
      setShowEditStaff(false);
      await loadStaffData();
    } catch (err) {
      toast.error(err.message || 'Failed to update staff');
    }
  };

  // Confirm Delete Staff
  const handleConfirmDeleteStaff = async () => {
    try {
      await api.delete(`/staff/${staff.id}`);
      toast.success('Staff deleted successfully');
      navigate('/staff');
    } catch (err) {
      toast.error(err.message || 'Failed to delete staff');
    }
  };

  // Open Edit Transaction Modal
  const openEditTransactionModal = (t) => {
    setEditTransaction(t);
    setEditTransForm({
      type: t.type,
      amount: String(t.amount || ''),
      deduction_amount: String(t.deduction_amount || '0'),
      payment_method: t.payment_method || 'cash',
      transaction_date: t.transaction_date || new Date().toISOString().split('T')[0],
      notes: t.notes || '',
    });
  };

  // Save Edit Transaction
  const handleSaveEditTransaction = async (e) => {
    e.preventDefault();
    const amt = parseFloat(editTransForm.amount);
    const ded = parseFloat(editTransForm.deduction_amount) || 0;
    if (!amt || amt <= 0) return toast.error('Please enter a valid amount');

    try {
      await api.put(`/staff-transactions/${editTransaction.id}`, {
        type: editTransForm.type,
        amount: amt,
        deduction_amount: ded,
        payment_method: editTransForm.payment_method,
        transaction_date: editTransForm.transaction_date,
        notes: editTransForm.notes,
      });

      toast.success('Transaction updated and balance adjusted successfully');
      setEditTransaction(null);
      await loadStaffData();
    } catch (err) {
      toast.error(err.message || 'Failed to update transaction');
    }
  };

  // Confirm Delete Transaction with Balance Reversal
  const handleConfirmDeleteTransaction = async () => {
    if (!deleteTransaction) return;
    try {
      await api.delete(`/staff-transactions/${deleteTransaction.id}`);
      toast.success('Transaction deleted and balance reversed successfully');
      setDeleteTransaction(null);
      await loadStaffData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete transaction');
    }
  };

  if (loading && !staff) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500 font-medium">Loading staff profile...</p>
        </div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/staff" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-emerald-600">
            <ArrowLeft className="h-4 w-4" /> Back to Staff
          </Link>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">Staff Member Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">The requested staff record could not be found or may have been deleted.</p>
          <Link to="/staff" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600">
            View All Staff
          </Link>
        </div>
      </div>
    );
  }

  // Financial Calculations
  const advanceBalance = parseFloat(staff.advanced_payment) || 0;
  const baseSalary = parseFloat(staff.salary_amount) || 0;

  const totalSalaryDisbursed = transactions
    .filter((t) => t.type === 'salary')
    .reduce((sum, t) => sum + (parseFloat(t.net_paid) || 0), 0);

  const totalAdvanceGiven = transactions
    .filter((t) => t.type === 'advance')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

  const totalAdvanceSettled = transactions.reduce((sum, t) => {
    if (t.type === 'salary') return sum + (parseFloat(t.deduction_amount) || 0);
    if (t.type === 'deduction') return sum + (parseFloat(t.amount) || 0);
    return sum;
  }, 0);

  // Filtered transactions for display
  const filteredTransactions = transactions.filter((t) => {
    const matchSearch =
      (t.notes || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.type || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.payment_method || '').toLowerCase().includes(search.toLowerCase());

    const matchTab =
      activeTab === 'all'
        ? true
        : activeTab === 'salaries'
        ? t.type === 'salary'
        : activeTab === 'advances'
        ? t.type === 'advance' || t.type === 'deduction' || t.type === 'adjustment'
        : true;

    const matchDateFrom = !dateFrom || t.transaction_date >= dateFrom;
    const matchDateTo = !dateTo || t.transaction_date <= dateTo;

    return matchSearch && matchTab && matchDateFrom && matchDateTo;
  });

  // Calculate Running Advance Balance in chronological order
  const chronological = [...transactions].sort((a, b) => {
    const d1 = a.transaction_date || '';
    const d2 = b.transaction_date || '';
    return d1.localeCompare(d2) || a.id - b.id;
  });

  let runningAdv = 0;
  const runningAdvMap = {};
  chronological.forEach((t) => {
    if (t.type === 'advance') {
      runningAdv += parseFloat(t.amount) || 0;
    } else if (t.type === 'salary') {
      runningAdv = Math.max(0, runningAdv - (parseFloat(t.deduction_amount) || 0));
    } else if (t.type === 'deduction') {
      runningAdv = Math.max(0, runningAdv - (parseFloat(t.amount) || 0));
    } else if (t.type === 'adjustment') {
      runningAdv = parseFloat(t.amount) || 0;
    }
    runningAdvMap[t.id] = runningAdv;
  });

  return (
    <div className="space-y-6">
      {/* ── Top Bar & Breadcrumb ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/staff')}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
            title="Back to Staff"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Link to="/staff" className="text-xs font-semibold text-gray-500 hover:text-emerald-600">
                Staff
              </Link>
              <span className="text-xs text-gray-400">/</span>
              <span className="text-xs font-semibold text-gray-900">{staff.name}</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Staff Profile & Transaction History</h1>
          </div>
        </div>

        {/* Top Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={openEditStaffModal}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <Edit3 className="h-4 w-4 text-gray-500" /> Edit Profile
          </button>
          {advanceBalance > 0 && (
            <button
              onClick={openDeductAdvanceModal}
              className="flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm font-semibold text-blue-800 shadow-sm transition-colors hover:bg-blue-100"
            >
              <RotateCcw className="h-4 w-4" /> Settle Advance
            </button>
          )}
          <button
            onClick={() => setShowDeleteStaff(true)}
            className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50"
            title="Delete Staff"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Staff Header Profile Card ── */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <div className={clsx('flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-2xl font-bold', avatarColors[staff.id % avatarColors.length])}>
              {staff.name.charAt(0).toUpperCase()}
            </div>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{staff.name}</h2>
                {advanceBalance > 0 ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-bold text-amber-700">
                    <CreditCard className="h-3.5 w-3.5 text-amber-500" /> Active Advance: {fmtMoney(advanceBalance)}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> No Advance Due
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-800">{staff.phone || 'No phone'}</span>
                </div>
                {staff.address && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{staff.address}</span>
                  </div>
                )}
                {staff.age && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Age:</span>
                    <span className="font-medium text-gray-700">{staff.age} yrs</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-500">Joined: {fmtDate(staff.created_at)}</span>
                </div>
              </div>

              {staff.notes && (
                <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600 max-w-2xl">
                  <span className="font-semibold text-gray-700">Designation / Note: </span>
                  {staff.notes}
                </div>
              )}
            </div>
          </div>

          {/* Quick Transaction Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4 md:border-0 md:pt-0">
            <button
              onClick={openPaySalaryModal}
              className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.97]"
            >
              <Banknote className="h-4 w-4" /> Pay Salary
            </button>
            <button
              onClick={openPayAdvanceModal}
              className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-amber-600 active:scale-[0.97]"
            >
              <CreditCard className="h-4 w-4" /> Give Advance
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI Financial Overview Cards ── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Base Monthly Salary */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Base Monthly Salary</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <Banknote className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-emerald-600">{fmtMoney(baseSalary)}</p>
          <p className="mt-2 text-xs text-gray-500">Designated monthly base rate</p>
        </div>

        {/* Current Active Advance */}
        <div className={clsx(
          'rounded-2xl border p-5 shadow-sm transition-all',
          advanceBalance > 0 ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200 bg-white'
        )}>
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Current Advance Balance</p>
            <div className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-xl',
              advanceBalance > 0 ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'
            )}>
              <CreditCard className="h-4 w-4" />
            </div>
          </div>
          <p className={clsx(
            'mt-2 text-2xl font-extrabold',
            advanceBalance > 0 ? 'text-amber-600' : 'text-gray-900'
          )}>
            {fmtMoney(advanceBalance)}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-gray-500">{advanceBalance > 0 ? 'Outstanding loan' : 'Fully settled'}</span>
            {advanceBalance > 0 && (
              <button onClick={openDeductAdvanceModal} className="font-bold text-amber-700 hover:underline">
                Settle &rarr;
              </button>
            )}
          </div>
        </div>

        {/* Total Salaries Paid */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Salaries Disbursed</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
              <Coins className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-gray-900">{fmtMoney(totalSalaryDisbursed)}</p>
          <p className="mt-2 text-xs text-gray-500">
            {transactions.filter((t) => t.type === 'salary').length} salary payout{transactions.filter((t) => t.type === 'salary').length === 1 ? '' : 's'}
          </p>
        </div>

        {/* Total Advances Taken */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Advances Given</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
              <History className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-blue-600">{fmtMoney(totalAdvanceGiven)}</p>
          <p className="mt-2 text-xs text-gray-500">
            {fmtMoney(totalAdvanceSettled)} settled over time
          </p>
        </div>
      </div>

      {/* ── Tabs Navigation ── */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
        <button
          onClick={() => setActiveTab('all')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'all' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <FileSpreadsheet className="h-4 w-4" /> All Transactions & Ledger ({transactions.length})
        </button>
        <button
          onClick={() => setActiveTab('salaries')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'salaries' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Banknote className="h-4 w-4" /> Salary Payouts ({transactions.filter((t) => t.type === 'salary').length})
        </button>
        <button
          onClick={() => setActiveTab('advances')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'advances' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <CreditCard className="h-4 w-4" /> Advance History ({transactions.filter((t) => t.type !== 'salary').length})
        </button>
      </div>

      {/* ── Filters & Search ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search transactions by notes or payment method..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <Printer className="h-3.5 w-3.5" /> Print Statement
          </button>
        </div>
      </div>

      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onApply={loadStaffData}
        onReset={() => {
          setDateFrom('');
          setDateTo('');
          setSearch('');
        }}
      />

      {/* ── Transactions Table ── */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Date / ID</th>
                <th className="px-6 py-3 whitespace-nowrap">Transaction Type</th>
                <th className="px-6 py-3 whitespace-nowrap">Notes & Breakdown</th>
                <th className="px-6 py-3 whitespace-nowrap">Method</th>
                <th className="px-6 py-3 whitespace-nowrap text-right">Advance Change</th>
                <th className="px-6 py-3 whitespace-nowrap text-right">Net Payout</th>
                <th className="px-6 py-3 whitespace-nowrap text-right">Running Advance</th>
                <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    <FileSpreadsheet className="mx-auto mb-2 h-10 w-10 text-gray-300" />
                    No transactions found for this staff member.
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((t) => {
                  const amt = parseFloat(t.amount) || 0;
                  const ded = parseFloat(t.deduction_amount) || 0;
                  const net = parseFloat(t.net_paid) || 0;
                  const runningAtThisPoint = runningAdvMap[t.id] ?? 0;

                  return (
                    <tr key={t.id} className="group transition-colors hover:bg-gray-50/50">
                      {/* Date / ID */}
                      <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-gray-900">{fmtDate(t.transaction_date)}</div>
                        <div className="text-[11px] text-gray-400 font-mono">TX-{t.id}</div>
                      </td>

                      {/* Transaction Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {t.type === 'salary' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                            <Banknote className="h-3 w-3" /> Salary Payment
                          </span>
                        )}
                        {t.type === 'advance' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                            <CreditCard className="h-3 w-3" /> Advance Given
                          </span>
                        )}
                        {t.type === 'deduction' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                            <RotateCcw className="h-3 w-3" /> Advance Settled
                          </span>
                        )}
                        {t.type === 'adjustment' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 border border-purple-200 px-2.5 py-0.5 text-xs font-semibold text-purple-700">
                            Adjustment
                          </span>
                        )}
                      </td>

                      {/* Notes & Breakdown */}
                      <td className="px-6 py-4 text-xs text-gray-600 max-w-sm">
                        <div className="font-medium text-gray-900 truncate">{t.notes || '-'}</div>
                        {t.type === 'salary' && ded > 0 && (
                          <div className="text-[11px] text-amber-600 font-mono mt-0.5">
                            Gross {fmtMoney(amt)} - Ded {fmtMoney(ded)}
                          </div>
                        )}
                      </td>

                      {/* Payment Method */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium capitalize text-gray-700">
                          {t.payment_method || 'cash'}
                        </span>
                      </td>

                      {/* Advance Change (+ / -) */}
                      <td className="px-6 py-4 whitespace-nowrap text-right font-mono text-xs">
                        {t.type === 'advance' && (
                          <span className="font-bold text-amber-600">+{fmtMoney(amt)}</span>
                        )}
                        {t.type === 'salary' && ded > 0 && (
                          <span className="font-bold text-emerald-600">-{fmtMoney(ded)}</span>
                        )}
                        {t.type === 'deduction' && (
                          <span className="font-bold text-blue-600">-{fmtMoney(amt)}</span>
                        )}
                        {t.type === 'salary' && ded === 0 && (
                          <span className="text-gray-400">-</span>
                        )}
                        {t.type === 'adjustment' && (
                          <span className="font-bold text-purple-600">{fmtMoney(amt)}</span>
                        )}
                      </td>

                      {/* Net Payout */}
                      <td className="px-6 py-4 whitespace-nowrap text-right font-extrabold text-gray-900 text-sm">
                        {net > 0 ? fmtMoney(net) : '-'}
                      </td>

                      {/* Running Advance Balance */}
                      <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-amber-700 text-xs">
                        {fmtMoney(runningAtThisPoint)}
                      </td>

                      {/* Actions: Edit & Delete */}
                      <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => openEditTransactionModal(t)}
                            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-amber-50 hover:text-amber-600"
                            title="Edit Transaction"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTransaction(t)}
                            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete Transaction & Reverse Balance"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── PAY SALARY MODAL ── */}
      {showPaySalary &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPaySalary(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                    <Banknote className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Pay Salary</h3>
                    <p className="text-xs text-gray-500">{staff.name}</p>
                  </div>
                </div>
                <button onClick={() => setShowPaySalary(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handlePaySalary} className="px-6 py-5 space-y-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Gross Salary (Rs.) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={salaryForm.amount}
                    onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                {/* Advance Deduction if staff has advance */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Deduct from Advance (Rs.)
                    </label>
                    <span className="text-[11px] text-amber-600 font-semibold">
                      Max: Rs. {advanceBalance.toLocaleString('en-PK')}
                    </span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={advanceBalance}
                    value={salaryForm.deduction_amount}
                    onChange={(e) => setSalaryForm({ ...salaryForm, deduction_amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                  />
                </div>

                {/* Net Disbursed Preview Banner */}
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between text-xs">
                  <div>
                    <span className="text-gray-600 block">Net Disbursed Cash / Bank:</span>
                    <span className="text-base font-extrabold text-emerald-700">
                      {fmtMoney(Math.max(0, (parseFloat(salaryForm.amount) || 0) - (parseFloat(salaryForm.deduction_amount) || 0)))}
                    </span>
                  </div>
                  {parseFloat(salaryForm.deduction_amount) > 0 && (
                    <div className="text-right">
                      <span className="text-gray-500 block">Remaining Advance:</span>
                      <span className="font-bold text-amber-700">
                        {fmtMoney(Math.max(0, advanceBalance - (parseFloat(salaryForm.deduction_amount) || 0)))}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method *</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSalaryForm({ ...salaryForm, payment_method: 'cash' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all',
                        salaryForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <Wallet className="h-4 w-4" /> Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalaryForm({ ...salaryForm, payment_method: 'bank' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all',
                        salaryForm.payment_method === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <CreditCard className="h-4 w-4" /> Bank
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date *</label>
                  <input
                    type="date"
                    value={salaryForm.transaction_date}
                    onChange={(e) => setSalaryForm({ ...salaryForm, transaction_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    value={salaryForm.notes}
                    onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPaySalary(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <Banknote className="h-4 w-4" /> Confirm & Pay Salary
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── GIVE ADVANCE MODAL ── */}
      {showPayAdvance &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPayAdvance(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <CreditCard className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Give Advance Payment</h3>
                    <p className="text-xs text-gray-500">{staff.name}</p>
                  </div>
                </div>
                <button onClick={() => setShowPayAdvance(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handlePayAdvance} className="px-6 py-5 space-y-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance Amount (Rs.) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={advanceForm.amount}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                  <span className="font-semibold block">New Total Advance Balance:</span>
                  <span className="text-sm font-bold text-amber-900">
                    {fmtMoney(advanceBalance + (parseFloat(advanceForm.amount) || 0))}
                  </span>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method *</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setAdvanceForm({ ...advanceForm, payment_method: 'cash' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all',
                        advanceForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <Wallet className="h-4 w-4" /> Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdvanceForm({ ...advanceForm, payment_method: 'bank' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-semibold transition-all',
                        advanceForm.payment_method === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <CreditCard className="h-4 w-4" /> Bank
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date *</label>
                  <input
                    type="date"
                    value={advanceForm.transaction_date}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, transaction_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    value={advanceForm.notes}
                    onChange={(e) => setAdvanceForm({ ...advanceForm, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPayAdvance(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-600"
                  >
                    <CreditCard className="h-4 w-4" /> Confirm & Give Advance
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── SETTLE / DEDUCT ADVANCE MODAL ── */}
      {showDeductAdvance &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeductAdvance(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                    <RotateCcw className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Settle / Deduct Advance</h3>
                    <p className="text-xs text-gray-500">{staff.name}</p>
                  </div>
                </div>
                <button onClick={() => setShowDeductAdvance(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleDeductAdvance} className="px-6 py-5 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Settlement Amount (Rs.) *</label>
                    <span className="text-[11px] text-blue-600 font-semibold">Active: {fmtMoney(advanceBalance)}</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={advanceBalance}
                    value={deductForm.amount}
                    onChange={(e) => setDeductForm({ ...deductForm, amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date *</label>
                  <input
                    type="date"
                    value={deductForm.transaction_date}
                    onChange={(e) => setDeductForm({ ...deductForm, transaction_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    value={deductForm.notes}
                    onChange={(e) => setDeductForm({ ...deductForm, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowDeductAdvance(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    <RotateCcw className="h-4 w-4" /> Confirm Settlement
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── EDIT TRANSACTION MODAL ── */}
      {editTransaction &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEditTransaction(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                    <Edit3 className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Transaction</h3>
                    <p className="text-xs text-gray-500 font-mono">TX-{editTransaction.id} &bull; {editTransaction.type.toUpperCase()}</p>
                  </div>
                </div>
                <button onClick={() => setEditTransaction(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSaveEditTransaction} className="px-6 py-5 space-y-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Transaction Type</label>
                  <select
                    value={editTransForm.type}
                    onChange={(e) => setEditTransForm({ ...editTransForm, type: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none capitalize"
                  >
                    <option value="salary">Salary Payment</option>
                    <option value="advance">Advance Given</option>
                    <option value="deduction">Advance Settled / Deducted</option>
                    <option value="adjustment">Balance Adjustment</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    {editTransForm.type === 'salary' ? 'Gross Salary Amount (Rs.) *' : 'Amount (Rs.) *'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editTransForm.amount}
                    onChange={(e) => setEditTransForm({ ...editTransForm, amount: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                {editTransForm.type === 'salary' && (
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Advance Deduction Amount (Rs.)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editTransForm.deduction_amount}
                      onChange={(e) => setEditTransForm({ ...editTransForm, deduction_amount: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Payment Method *</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEditTransForm({ ...editTransForm, payment_method: 'cash' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-semibold transition-all',
                        editTransForm.payment_method === 'cash' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <Wallet className="h-4 w-4" /> Cash
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditTransForm({ ...editTransForm, payment_method: 'bank' })}
                      className={clsx(
                        'flex-1 flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-2.5 text-sm font-semibold transition-all',
                        editTransForm.payment_method === 'bank' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      )}
                    >
                      <CreditCard className="h-4 w-4" /> Bank
                    </button>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Date *</label>
                  <input
                    type="date"
                    value={editTransForm.transaction_date}
                    onChange={(e) => setEditTransForm({ ...editTransForm, transaction_date: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Notes</label>
                  <textarea
                    value={editTransForm.notes}
                    onChange={(e) => setEditTransForm({ ...editTransForm, notes: e.target.value })}
                    rows={2}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditTransaction(null)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <Save className="h-4 w-4" /> Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── DELETE TRANSACTION CONFIRM MODAL ── */}
      {deleteTransaction &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteTransaction(null)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Transaction?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete transaction <strong>TX-{deleteTransaction.id}</strong> (
                {deleteTransaction.type.toUpperCase()} of {fmtMoney(deleteTransaction.amount)})?
              </p>

              {/* Automatic reversal notification */}
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-left text-xs text-amber-800">
                <span className="font-bold block mb-1">Balance Reversal Action:</span>
                {deleteTransaction.type === 'advance' && (
                  <span>The advance balance will be reduced by <strong>{fmtMoney(deleteTransaction.amount)}</strong> and the cashbook expense will be deleted.</span>
                )}
                {deleteTransaction.type === 'salary' && (
                  <span>
                    The salary expense will be removed.
                    {parseFloat(deleteTransaction.deduction_amount) > 0 && (
                      <> Any deducted advance of <strong>{fmtMoney(deleteTransaction.deduction_amount)}</strong> will be restored back to staff advance balance.</>
                    )}
                  </span>
                )}
                {deleteTransaction.type === 'deduction' && (
                  <span>The advance balance will be increased back by <strong>{fmtMoney(deleteTransaction.amount)}</strong>.</span>
                )}
              </div>

              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setDeleteTransaction(null)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteTransaction}
                  className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete & Reverse
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ── EDIT STAFF PROFILE MODAL ── */}
      {showEditStaff &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditStaff(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Staff Profile</h3>
                <button onClick={() => setShowEditStaff(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSaveStaff} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Staff Name *</label>
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone Number</label>
                    <input
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Age</label>
                    <input
                      type="number"
                      value={editForm.age}
                      onChange={(e) => setEditForm({ ...editForm, age: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                    <input
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Salary Amount (Rs.)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.salary_amount}
                      onChange={(e) => setEditForm({ ...editForm, salary_amount: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance Balance (Rs.)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.advanced_payment}
                      onChange={(e) => setEditForm({ ...editForm, advanced_payment: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note / Designation</label>
                    <textarea
                      value={editForm.notes || ''}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                    />
                  </div>
                </div>

                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-5">
                  <button
                    type="button"
                    onClick={() => setShowEditStaff(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <Save className="h-4 w-4" /> Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* ── DELETE STAFF CONFIRM MODAL ── */}
      {showDeleteStaff &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteStaff(false)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Staff Member?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete <strong>{staff.name}</strong> and all associated transaction records? This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setShowDeleteStaff(false)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeleteStaff}
                  className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ══════ DEDICATED PRINT CONTAINER FOR STAFF STATEMENT ══════ */}
      {createPortal(
        <div id="printable-statement-export" className="hidden print:block" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 30, color: '#1a1a1a' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #10b981', paddingBottom: 16 }}>
            <div>
              <PharmacyLogo logo={settings?.pharmacy_logo} alt="Shop logo" className="h-10 w-10 rounded-lg mb-2" imageClassName="h-10 w-10 rounded-lg object-contain mb-2" />
              <h1 style={{ fontSize: 22, color: '#10b981', margin: 0, fontWeight: 800 }}>{settings?.pharmacy_name || 'Gaza Pharmacy'}</h1>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Staff Account Statement & Ledger</p>
              {settings?.pharmacy_address && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{settings.pharmacy_address}</p>}
              {settings?.pharmacy_phone && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Tel: {settings.pharmacy_phone}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{staff?.name}</p>
              <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Role: {staff?.role || 'Staff'}</p>
              {staff?.phone && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Phone: {staff.phone}</p>}
              <p style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0' }}>Generated: {new Date().toLocaleString()}</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
              <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Base Salary</p>
              <p style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 0' }}>{fmtMoney(staff?.base_salary || 0)}</p>
            </div>
            <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
              <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Advance Outstanding</p>
              <p style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 0', color: (staff?.advance_balance || 0) > 0 ? '#d97706' : '#111827' }}>{fmtMoney(staff?.advance_balance || 0)}</p>
            </div>
            <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
              <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Salary Paid</p>
              <p style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 0', color: '#059669' }}>{fmtMoney(totalSalaryDisbursed)}</p>
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 16 }}>
            <thead>
              <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px' }}>Date</th>
                <th style={{ padding: '8px 10px' }}>Type</th>
                <th style={{ padding: '8px 10px' }}>Payment Method</th>
                <th style={{ padding: '8px 10px' }}>Notes / Reference</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((t, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '8px 10px', color: '#4b5563' }}>{fmtDate(t.transaction_date || t.created_at)}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 600, textTransform: 'capitalize' }}>{t.type?.replace('_', ' ')}</td>
                  <td style={{ padding: '8px 10px', textTransform: 'capitalize', color: '#4b5563' }}>{t.payment_method || 'cash'}</td>
                  <td style={{ padding: '8px 10px', color: '#6b7280' }}>{t.notes || '-'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{fmtMoney(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 24, textAlign: 'center', fontSize: 10, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
            {settings?.pharmacy_name || 'Gaza Pharmacy'} Management System — Statement Generated on {new Date().toLocaleString()}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
